import { Box, Divider, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { useEffect, useRef, useState } from "react";

import { AddressSection } from "./ProfileV2/AddressSection";
import { BasicsSection } from "./ProfileV2/BasicsSection";
import { FacilityContactsSection } from "./ProfileV2/FacilityContactsSection";
import { FacilityCredentialsSection } from "./ProfileV2/FacilityCredentialsSection";
import { FacilityDocumentsSection } from "./ProfileV2/FacilityDocumentsSection";
import { FacilityHeaderV2, SECTION_NAV_ID } from "./ProfileV2/FacilityHeaderV2";
import { LocalAgenciesSection } from "./ProfileV2/LocalAgenciesSection";
import { QuickLinks } from "./ProfileV2/QuickLinks";
import { RcraProfileSection } from "./ProfileV2/RcraProfileSection";
import { StateInformationSection } from "./ProfileV2/StateInformationSection";
import { TasksSnapshot } from "./ProfileV2/TasksSnapshot";
import { useFacility } from "./useFacility";

export const FacilityProfileV2: React.FC = () => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const { data, loading, error } = useFacility();
  const [navBottom, setNavBottom] = useState(0);
  const [sidebarHeight, setSidebarHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 0
  );
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const OverviewSectionSkeleton = () => (
    <Box>
      <Skeleton variant="text" width="40%" height={28} />
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        <Skeleton variant="text" width="92%" />
        <Skeleton variant="text" width="75%" />
        <Skeleton variant="text" width="88%" />
        <Skeleton variant="text" width="60%" />
      </Stack>
    </Box>
  );

  // Dynamically measure the bottom edge of the sticky header (tabs + section chips)
  // so the Quick Links sidebar can stick just below it. The header height varies
  // depending on whether the section chips wrap to multiple lines, so we use a
  // ResizeObserver to recalculate whenever it changes (e.g. on window resize).
  useEffect(() => {
    const nav = document.getElementById(SECTION_NAV_ID);
    if (!nav) return;

    const updateHeight = () => {
      const rect = nav.getBoundingClientRect();
      setNavBottom(rect.bottom);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [loading]);

  // Track the sidebar's height so we can switch from stick-under-header to
  // stick-when-bottom-hits-viewport-bottom for short viewports.
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const update = () => setSidebarHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  // Track viewport height so the sticky top recalculates when the window resizes.
  useEffect(() => {
    const update = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const sidebarBottomGap = 12;
  const stickTop =
    sidebarHeight > 0 && viewportHeight > 0
      ? Math.min(navBottom + 12, viewportHeight - sidebarHeight - sidebarBottomGap)
      : navBottom + 12;

  useBreadcrumb([
    {
      label: "Facilities",
      to: `/o/${tenant?.id}/facilities`,
    },
    {
      label: data?.facility?.name ?? "",
    },
  ]);

  if (error) {
    throw error;
  }

  if (loading) {
    return (
      <Box
        sx={{ background: blueGrey[theme.palette.mode === "light" ? 50 : 900], minHeight: "100vh" }}
      >
        <Box sx={{ px: 2, pt: 2 }}>
          <Paper elevation={0} sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Skeleton variant="text" width="28%" height={34} />
              <Skeleton variant="text" width="45%" />
              <Skeleton variant="rectangular" height={42} sx={{ borderRadius: 1 }} />
            </Stack>
          </Paper>
        </Box>

        <Stack direction="row" spacing={2} sx={{ p: 2 }}>
          <Paper elevation={0} sx={{ p: 3, flex: 1, minWidth: 0 }}>
            <Typography variant="h5" mb={5}>
              <Skeleton variant="text" width={260} />
            </Typography>

            <OverviewSectionSkeleton />
            <Divider sx={{ my: 5 }} />
            <OverviewSectionSkeleton />
            <Divider sx={{ my: 5 }} />
            <OverviewSectionSkeleton />
            <Divider sx={{ my: 5 }} />
            <OverviewSectionSkeleton />
            <Divider sx={{ my: 5 }} />
            <OverviewSectionSkeleton />
            <Divider sx={{ my: 5 }} />
            <OverviewSectionSkeleton />
            <Divider sx={{ my: 5 }} />
            <OverviewSectionSkeleton />
          </Paper>

          <Stack spacing={2} sx={{ display: { xs: "none", md: "flex" }, width: 320 }}>
            {featureFlags[FeatureFlagKey.TasksModule] && (
              <Paper elevation={0} sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Skeleton variant="text" width="55%" height={26} />
                  <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 1 }} />
                </Stack>
              </Paper>
            )}
            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={1.25}>
                <Skeleton variant="text" width="45%" height={26} />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="70%" />
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{ background: blueGrey[theme.palette.mode === "light" ? 50 : 900], minHeight: "100vh" }}
    >
      <FacilityHeaderV2 />

      <Stack direction="row" spacing={2} sx={{ p: 2 }}>
        <Paper elevation={0} sx={{ p: 3, flex: 1, minWidth: 0 }}>
          <Typography variant="h5" mb={5}>
            Facility Overview
          </Typography>
          <BasicsSection />
          <Divider sx={{ my: 5 }} />
          <AddressSection />
          <Divider sx={{ my: 5 }} />
          <LocalAgenciesSection />
          <Divider sx={{ my: 5 }} />
          <StateInformationSection />
          <Divider sx={{ my: 5 }} />
          <FacilityContactsSection />
          {data?.facility?.rcraProfileEnabled && (
            <>
              <Divider sx={{ my: 5 }} />
              <RcraProfileSection />
            </>
          )}
          <Divider sx={{ my: 5 }} />
          <FacilityDocumentsSection />
          <Divider sx={{ my: 5 }} />
          <FacilityCredentialsSection />
        </Paper>
        <Stack
          ref={sidebarRef}
          spacing={2}
          sx={{
            alignSelf: "flex-start",
            position: "sticky",
            display: { xs: "none", md: "flex" },
            top: `${stickTop}px`,
          }}
        >
          {featureFlags[FeatureFlagKey.TasksModule] && (
            <Paper elevation={0} sx={{ p: 2 }}>
              <TasksSnapshot />
            </Paper>
          )}
          <Paper elevation={0} sx={{ p: 2 }}>
            <QuickLinks />
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};
